/**
 * Hyperliquid action signing (L1 actions + user-signed actions), byte-compatible with hyperliquid-python-sdk
 * (`hyperliquid/utils/signing.py`). Golden vectors: `shared/fixtures/golden-signing.json` (re-exported by `__fixtures__/golden.ts`), generated by `web/scripts/gen_golden.py`.
 *
 * L1 action (order, cancel, ...):
 *   hash = keccak256( msgpack(action) ‖ nonce u64 BE ‖ (0x00 | 0x01 ‖ vault 20B) ‖ [0x00 ‖ expiresAfter u64 BE] )
 *   EIP-712 domain {name:"Exchange", version:"1", chainId:1337, verifyingContract:0x0}, Agent{source, connectionId}
 *   source "a" = mainnet, "b" = testnet.
 * Builder codes (#770): an order action may carry `builder: {b, f}`; approveBuilderFee is user-signed.
 * User-signed action (approveAgent, approveBuilderFee, transfers): domain HyperliquidSignTransaction, chainId = signatureChainId
 * (SDK default 0x66eee; any chain is accepted), the action itself is the message, plus `signatureChainId` and
 * `hyperliquidChain`.
 */
import { encode } from '@msgpack/msgpack';
import { concat, hexToBytes, keccak256, numberToBytes, type Hex } from 'viem';

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as const;
export const L1_CHAIN_ID = 1337;
export const USER_SIGNED_CHAIN_ID_HEX = '0x66eee' as const;

export interface TypedDataField {
  name: string;
  type: string;
}

export interface Eip712Domain {
  name: string;
  version: string;
  chainId: number;
  verifyingContract: Hex;
}

export interface TypedDataPayload {
  domain: Eip712Domain;
  types: Record<string, TypedDataField[]>;
  primaryType: string;
  message: Record<string, unknown>;
}

export interface Signature {
  r: Hex;
  s: Hex;
  v: number;
}

const EIP712_DOMAIN_TYPE: TypedDataField[] = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' },
];

/**
 * Rejects values that msgpack would encode differently from Python, or that Python's SDK never produces:
 * non-integer numbers (HL actions carry prices/sizes as strings), unsafe integers, undefined, bigint, binary.
 */
export function assertMsgpackSafe(v: unknown, path = 'action'): void {
  if (v === null || typeof v === 'string' || typeof v === 'boolean') return;
  if (typeof v === 'number') {
    if (!Number.isSafeInteger(v)) throw new Error(`${path}: non-integer or unsafe number ${v} (use wire strings)`);
    return;
  }
  if (Array.isArray(v)) {
    v.forEach((x, i) => assertMsgpackSafe(x, `${path}[${i}]`));
    return;
  }
  if (typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) assertMsgpackSafe(x, `${path}.${k}`);
    return;
  }
  throw new Error(`${path}: unsupported value type ${typeof v}`);
}

/** msgpack bytes of an action, key order = JS insertion order (same as Python dict order). */
export function packAction(action: unknown): Uint8Array {
  assertMsgpackSafe(action);
  return encode(action);
}

function u64be(n: number | bigint, what: string): Uint8Array {
  const b = typeof n === 'bigint' ? n : BigInt(n);
  if (typeof n === 'number' && !Number.isSafeInteger(n)) throw new Error(`${what} must be a safe integer`);
  if (b < 0n || b >= 1n << 64n) throw new Error(`${what} out of uint64 range`);
  return numberToBytes(b, { size: 8 });
}

/** Same as the SDK's `action_hash(action, vault_address, nonce, expires_after)`. */
export function actionHash(
  action: unknown,
  vaultAddress: Hex | string | null,
  nonce: number | bigint,
  expiresAfter?: number | bigint | null,
): Hex {
  const parts: Uint8Array[] = [packAction(action), u64be(nonce, 'nonce')];
  if (vaultAddress == null) {
    parts.push(new Uint8Array([0]));
  } else {
    const vb = hexToBytes(vaultAddress as Hex);
    if (vb.length !== 20) throw new Error('vaultAddress must be 20 bytes');
    parts.push(new Uint8Array([1]), vb);
  }
  if (expiresAfter != null) parts.push(new Uint8Array([0]), u64be(expiresAfter, 'expiresAfter'));
  return keccak256(concat(parts));
}

/** EIP-712 payload for an L1 action (phantom agent). `source` = env.signingSource ("a" mainnet, "b" testnet). */
export function l1TypedData(hash: Hex, source: 'a' | 'b'): TypedDataPayload {
  return {
    domain: { chainId: L1_CHAIN_ID, name: 'Exchange', verifyingContract: ZERO_ADDRESS, version: '1' },
    types: {
      Agent: [
        { name: 'source', type: 'string' },
        { name: 'connectionId', type: 'bytes32' },
      ],
      EIP712Domain: EIP712_DOMAIN_TYPE,
    },
    primaryType: 'Agent',
    message: { source, connectionId: hash },
  };
}

/**
 * EIP-712 payload for a user-signed action. Like the SDK this MUTATES `action`, setting `signatureChainId` and
 * `hyperliquidChain`, so the same object is what you POST.
 *
 * `signatureChainId` (hex) defaults to the SDK's 0x66eee. Hyperliquid accepts any chain here ("the chain used by
 * the wallet to sign and can be any chain", SDK signing.py); the domain chainId always equals it. Agent-wallet
 * enabling (#688) passes the wallet's CURRENT chain so the wallet never has to switch.
 */
export function userSignedTypedData(
  primaryType: string,
  types: TypedDataField[],
  action: Record<string, unknown>,
  isMainnet: boolean,
  signatureChainId: Hex = USER_SIGNED_CHAIN_ID_HEX,
): TypedDataPayload {
  const chainId = parseInt(signatureChainId, 16);
  if (!Number.isSafeInteger(chainId) || chainId <= 0) throw new Error(`invalid signatureChainId ${signatureChainId}`);
  action.signatureChainId = `0x${chainId.toString(16)}`;
  action.hyperliquidChain = isMainnet ? 'Mainnet' : 'Testnet';
  return {
    domain: {
      name: 'HyperliquidSignTransaction',
      version: '1',
      chainId,
      verifyingContract: ZERO_ADDRESS,
    },
    types: { [primaryType]: types, EIP712Domain: EIP712_DOMAIN_TYPE },
    primaryType,
    message: action,
  };
}

export const APPROVE_AGENT_TYPES: TypedDataField[] = [
  { name: 'hyperliquidChain', type: 'string' },
  { name: 'agentAddress', type: 'address' },
  { name: 'agentName', type: 'string' },
  { name: 'nonce', type: 'uint64' },
];
export const APPROVE_AGENT_PRIMARY_TYPE = 'HyperliquidTransaction:ApproveAgent';

/** approveAgent action in SDK key order (type, agentAddress, agentName, nonce). */
export function approveAgentAction(agentAddress: Hex, agentName: string, nonce: number): Record<string, unknown> {
  return { type: 'approveAgent', agentAddress, agentName, nonce };
}

export const APPROVE_BUILDER_FEE_TYPES: TypedDataField[] = [
  { name: 'hyperliquidChain', type: 'string' },
  { name: 'maxFeeRate', type: 'string' },
  { name: 'builder', type: 'address' },
  { name: 'nonce', type: 'uint64' },
];
export const APPROVE_BUILDER_FEE_PRIMARY_TYPE = 'HyperliquidTransaction:ApproveBuilderFee';

/**
 * approveBuilderFee action (#770) in SDK key order (maxFeeRate, builder, nonce, type), as
 * `Exchange.approve_builder_fee` builds it. `maxFeeRate` is a percent string ("0.01%"); `builder` is passed through
 * unchanged (the SDK does not lowercase it here; EIP-712 encodes an address as 20 bytes, so its case does not change
 * the signature, but it is part of the posted JSON).
 */
export function approveBuilderFeeAction(maxFeeRate: string, builder: Hex | string, nonce: number): Record<string, unknown> {
  if (!/^\d+(\.\d+)?%$/.test(maxFeeRate)) throw new Error(`maxFeeRate must be a percent string like "0.01%": ${maxFeeRate}`);
  if (!/^0x[0-9a-fA-F]{40}$/.test(builder)) throw new Error(`invalid builder address ${builder}`);
  return { maxFeeRate, builder, nonce, type: 'approveBuilderFee' };
}

/** 65-byte hex signature into {r, s, v} (v normalised to 27/28). */
export function splitSig(sig: string): Signature {
  const h = sig.startsWith('0x') ? sig.slice(2) : sig;
  if (!/^[0-9a-fA-F]{130}$/.test(h)) throw new Error('signature must be 65 bytes (130 hex chars)');
  let v = parseInt(h.slice(128, 130), 16);
  if (v < 27) v += 27;
  return { r: `0x${h.slice(0, 64)}`, s: `0x${h.slice(64, 128)}`, v };
}

// ---------------------------------------------------------------------------------------------------------------
// Wire formatting + action builders (hyperliquid-python-sdk order_request_to_order_wire / order_wires_to_order_action)

/** SDK `float_to_wire`: 8 decimals, refuse lossy rounding, "-0" → "0", strip trailing zeros. */
export function floatToWire(x: number): string {
  if (!Number.isFinite(x)) throw new Error(`floatToWire: not finite: ${x}`);
  let rounded = x.toFixed(8);
  if (Math.abs(Number(rounded) - x) >= 1e-12) throw new Error(`floatToWire causes rounding: ${x}`);
  if (/^-0\.?0*$/.test(rounded)) rounded = rounded.slice(1);
  // Decimal(rounded).normalize() formatted with :f → strip trailing zeros (and the dot).
  if (rounded.includes('.')) rounded = rounded.replace(/0+$/, '').replace(/\.$/, '');
  return rounded === '-0' ? '0' : rounded;
}

export type Tif = 'Gtc' | 'Ioc' | 'Alo';
export type Grouping = 'na' | 'normalTpsl' | 'positionTpsl';

export interface OrderInput {
  /** Asset id. For outcomes use `assetId()` from ./outcomes. */
  asset: number;
  isBuy: boolean;
  /** Number (formatted with floatToWire) or a pre-formatted wire string (e.g. from outcomes.toWirePx). */
  px: number | string;
  sz: number | string;
  reduceOnly?: boolean;
  tif: Tif;
  /** 16-byte client order id, 0x + 32 hex. */
  cloid?: Hex;
}

export interface OrderWire {
  a: number;
  b: boolean;
  p: string;
  s: string;
  r: boolean;
  t: { limit: { tif: Tif } };
  c?: Hex;
}

/**
 * Builder code on an order action (#770): `b` = builder address, LOWERCASE (the SDK's bulk_orders lowercases it
 * before signing); `f` = fee in TENTHS of a basis point (10 = 1 bp = 0.01%). The user must have approved at least
 * `f` for `b` with approveBuilderFee, or Hyperliquid rejects the order.
 */
export interface BuilderInfo {
  b: string;
  f: number;
}

export interface OrderAction {
  type: 'order';
  orders: OrderWire[];
  grouping: Grouping;
  builder?: BuilderInfo;
}

function wireNum(v: number | string, what: string): string {
  if (typeof v === 'number') return floatToWire(v);
  if (!/^\d+(\.\d+)?$/.test(v)) throw new Error(`${what}: invalid wire string ${v}`);
  return v;
}

/** Key order a, b, p, s, r, t, [c] exactly as the SDK builds it. */
export function orderWire(o: OrderInput): OrderWire {
  if (!Number.isSafeInteger(o.asset) || o.asset < 0) throw new Error(`invalid asset id ${o.asset}`);
  const w: OrderWire = {
    a: o.asset,
    b: o.isBuy,
    p: wireNum(o.px, 'px'),
    s: wireNum(o.sz, 'sz'),
    r: o.reduceOnly ?? false,
    t: { limit: { tif: o.tif } },
  };
  if (o.cloid !== undefined) {
    if (!/^0x[0-9a-fA-F]{32}$/.test(o.cloid)) throw new Error('cloid must be 0x + 32 hex chars');
    w.c = o.cloid;
  }
  return w;
}

/** Builder in the exact wire form: `{b: lowercase address, f: integer}`. Throws on a bad address or fee. */
export function builderWire(b: { b: string; f: number }): BuilderInfo {
  if (!/^0x[0-9a-fA-F]{40}$/.test(b.b)) throw new Error(`invalid builder address ${b.b}`);
  if (!Number.isSafeInteger(b.f) || b.f < 0) throw new Error(`builder fee must be a non-negative integer (tenths of a bp): ${b.f}`);
  return { b: b.b.toLowerCase(), f: b.f };
}

/**
 * SDK `order_wires_to_order_action(order_wires, builder, grouping)`: key order type, orders, grouping, [builder].
 * No builder (null/undefined) = no `builder` key at all.
 */
export function orderAction(orders: OrderInput[], grouping: Grouping = 'na', builder?: BuilderInfo | null): OrderAction {
  const action: OrderAction = { type: 'order', orders: orders.map(orderWire), grouping };
  if (builder) action.builder = builderWire(builder);
  return action;
}

export interface CancelInput {
  a: number;
  o: number;
}

export function cancelAction(cancels: CancelInput[]): { type: 'cancel'; cancels: CancelInput[] } {
  return { type: 'cancel', cancels: cancels.map(({ a, o }) => ({ a, o })) };
}
